import { useCallback, useMemo, useState } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { useAccessCan } from "@/modules/dashboard/settings/hooks/useAccessCan";
import { usePoliciesData } from "@/modules/dashboard/settings/hooks/useAccessData";
import { usePolicyUsage } from "@/modules/dashboard/settings/hooks/usePolicyUsage";
import type { AccessPolicy } from "@/modules/dashboard/settings/types/access.types";

import {
  ACCESS_FILTER_ALL,
  ACCESS_TABLE_PAGE_SIZE,
  type AccessScopeFilter,
} from "../constants/access.constants";
import { matchesScope, normalizeSearchTerm } from "../utils/catalogFilters";
import { clampPage, pageSlice } from "../utils/pageSlice";

/** The four buckets of the KPI strip. The strip IS the filter, so each key is also a filter value. */
export type PoliciesBucket = typeof ACCESS_FILTER_ALL | "allow" | "deny" | "unused";

export interface PolicyRow {
  policy: AccessPolicy;
  /** How many roles link this policy. Derived — the policies endpoint does not return the inverse relation. */
  usedBy: number;
  /** Distinct `resource` values across its statements; the second half of the "N permisos · M recursos" line. */
  resourceCount: number;
  /** Not protected AND in the caller's scope — the client-side mirror of the backend's `assertWritable`. */
  editable: boolean;
}

export type PoliciesBucketCounts = Record<PoliciesBucket, number>;

/** Which bucket a row belongs to. Exported for its test — see `matchesRoleBucket` for why. */
export function matchesPolicyBucket(row: PolicyRow, bucket: PoliciesBucket): boolean {
  if (bucket === "allow") return row.policy.effect === "allow";
  if (bucket === "deny") return row.policy.effect === "deny";
  if (bucket === "unused") return row.usedBy === 0;
  return true;
}

/**
 * Name, description and **`resource:action`**. The statements are searchable because the question an operator
 * actually arrives with is "which policy grants `cards:topup`?" — a name search cannot answer it, even though
 * the answer is right there in the row.
 */
export function matchesPolicySearch(policy: AccessPolicy, term: string): boolean {
  const needle = term.trim().toLowerCase();
  if (!needle) return true;
  return (
    policy.name.toLowerCase().includes(needle) ||
    (policy.description?.toLowerCase().includes(needle) ?? false) ||
    policy.statements.some((s) => `${s.resource}:${s.action}`.toLowerCase().includes(needle))
  );
}

/**
 * The policies catalog as the page consumes it: search, scope, bucket, pagination, the permission affordances
 * and — the part that cannot come from the API — how many roles link each policy.
 *
 * That last one matters twice over. `GET /internal/policies` returns the entity with no inverse relation, so
 * "used by N roles" is computed here from the roles catalog the module already holds (each role carries its
 * policies). And it is not decoration: `DELETE /internal/policies/:id` answers **409 while any role links
 * it**, so this number is what tells a reader whether deleting is even possible, before they try.
 *
 * Everything else is derived in memory for the same reason as roles — `QueryPoliciesDto` accepts `skip`,
 * `limit` and `organization_id` and nothing else.
 */
export function usePoliciesCatalog() {
  const { isInternalUser } = useAuth();
  const { canAll } = useAccessCan();
  const { policies, isLoading, hasNoAccess, load } = usePoliciesData();
  // "Used by N roles" and its honesty flags come from the shared hook, so this view and Settings → Políticas
  // cannot answer differently about the same policy. See `usePolicyUsage` for why the flags are not optional.
  const { usedByCount, usageLoading, usageUnknown, reloadUsage } = usePolicyUsage();

  const [query, setQueryState] = useState("");
  const [scope, setScopeState] = useState<AccessScopeFilter>(ACCESS_FILTER_ALL);
  const [bucket, setBucketState] = useState<PoliciesBucket>(ACCESS_FILTER_ALL);
  const [page, setPage] = useState(1);

  // Page resets live in the SETTER, never in an effect: an effect would first render page 4 of a 1-page
  // result — an empty table under a total that says otherwise.
  const setQuery = useCallback((next: string) => {
    setQueryState(next);
    setPage(1);
  }, []);
  const setScope = useCallback((next: AccessScopeFilter) => {
    setScopeState(next);
    setPage(1);
  }, []);
  const setBucket = useCallback((next: PoliciesBucket) => {
    setBucketState(next);
    setPage(1);
  }, []);

  const isEditable = useCallback(
    (policy: AccessPolicy): boolean => !policy.is_protected && (isInternalUser || !!policy.organization_id),
    [isInternalUser],
  );

  const rows = useMemo<PolicyRow[]>(
    () =>
      policies.map((policy) => ({
        policy,
        usedBy: usedByCount(policy.id),
        resourceCount: new Set(policy.statements.map((s) => s.resource)).size,
        editable: isEditable(policy),
      })),
    [policies, usedByCount, isEditable],
  );

  const scoped = useMemo(() => {
    const term = normalizeSearchTerm(query);
    return rows.filter(
      ({ policy }) => matchesScope(policy.organization_id, scope) && matchesPolicySearch(policy, term),
    );
  }, [rows, query, scope]);

  const counts = useMemo<PoliciesBucketCounts>(
    () => ({
      all: scoped.length,
      allow: scoped.filter((row) => matchesPolicyBucket(row, "allow")).length,
      deny: scoped.filter((row) => matchesPolicyBucket(row, "deny")).length,
      unused: scoped.filter((row) => matchesPolicyBucket(row, "unused")).length,
    }),
    [scoped],
  );

  const filtered = useMemo(
    () => (bucket === ACCESS_FILTER_ALL ? scoped : scoped.filter((row) => matchesPolicyBucket(row, bucket))),
    [scoped, bucket],
  );

  const safePage = clampPage(page, filtered.length);
  const paged = useMemo(() => pageSlice(filtered, page), [filtered, page]);

  const hasActiveFilters = query.trim() !== "" || scope !== ACCESS_FILTER_ALL || bucket !== ACCESS_FILTER_ALL;

  const clearFilters = useCallback(() => {
    setQueryState("");
    setScopeState(ACCESS_FILTER_ALL);
    setBucketState(ACCESS_FILTER_ALL);
    setPage(1);
  }, []);

  // A policy mutation also refreshes roles: a role embeds its policies' statements, so its effective matrix
  // and every "used by" tally would otherwise show a pre-edit copy.
  const reload = useCallback(async () => {
    await Promise.all([load(), reloadUsage()]);
  }, [load, reloadUsage]);

  return {
    rows: paged,
    total: filtered.length,
    counts,
    // The roles catalog gates the table too: rendering "unused" against a half-loaded catalog would flash a
    // warning badge on every row and then take it back.
    isLoading: isLoading || usageLoading,
    hasNoAccess,
    /**
     * The usage figures could not be read — denied or failed. Everything derived from them (the "unused"
     * badge, its KPI tile, whether deleting is possible) says so instead of reporting zero, which is the same
     * shape as "no role links this" and leads to the opposite action.
     */
    usageUnknown,
    usedByCount,
    reload,
    page: safePage,
    pageSize: ACCESS_TABLE_PAGE_SIZE,
    setPage,
    query,
    setQuery,
    scope,
    setScope,
    bucket,
    setBucket,
    hasActiveFilters,
    clearFilters,
    isEditable,
    // `:write`, not `:update` — `scopes[]` carries RBAC atoms and the backend guard collapses `update` to
    // `write`, so checking the composite would always be false.
    canCreate: canAll("policies:write"),
    canUpdate: canAll("policies:write"),
    canDelete: canAll("policies:delete"),
  };
}
